// src/components/Navbar.js
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import useAuth from "../hooks/useAuth";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isAuthenticated, user, logout, isLoading } = useAuth();

  const baseNavItems = [
    { path: "/interview", label: "Interview Prep" },
    { path: "/english-practice", label: "Practice English" },
    { path: "/pricing", label: "Pricing" },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleNavClick = (path) => {
    closeMobileMenu();
    // if (path === "/") {
    // resetInterview(); // Uncomment if needed
    // }
  };

  const handleLogout = () => {
    logout();
    closeMobileMenu();
    navigate("/"); // Redirect to home after logout
  };

  const authNavItems = isAuthenticated
    ? [{ action: handleLogout, label: "Logout" }]
    : [
        { path: "/login", label: "Login" },
        { path: "/register", label: "Sign Up" },
      ];

  return (
    <nav className="bg-gradient-to-r from-gray-800 to-gray-700 text-white shadow-md sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo/Brand */}
          <div className="flex items-center">
            <Link
              to={isAuthenticated ? "/dashboard" : "/"}
              className="flex items-center space-x-3"
              onClick={() => handleNavClick("/")}
            >
              <img
                src="/app-icon.jpeg" // Make sure this path is correct from public folder
                alt="Practap"
                className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg object-cover flex-shrink-0"
              />
              <span className="font-bold text-lg sm:text-xl tracking-tight hover:text-gray-200 transition-colors">
                Practap
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center">
            <div className="ml-10 flex items-baseline space-x-4">
              {baseNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 transform hover:scale-105
                    ${
                      location.pathname === item.path
                        ? "bg-gray-900 text-white shadow-lg"
                        : "text-gray-300 hover:bg-gray-700 hover:text-white"
                    }`}
                  onClick={() => handleNavClick(item.path)}
                >
                  {item.label}
                </Link>
              ))}
            </div>
            {/* Auth Links - Desktop */}
            {!isLoading && (
              <div className="ml-4 flex items-center space-x-4">
                {isAuthenticated && user && (
                  <span className="text-gray-300 text-sm">
                    Hi, {user.firstName || user.userName}!
                  </span>
                )}
                {authNavItems.map((item) =>
                  item.path ? (
                    <Link
                      key={item.label}
                      to={item.path}
                      className="px-3 py-2 rounded-md text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white"
                      onClick={() => handleNavClick(item.path)}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <button
                      key={item.label}
                      onClick={item.action}
                      className="px-3 py-2 rounded-md text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white"
                    >
                      {item.label}
                    </button>
                  )
                )}
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            {isAuthenticated && user && (
              <span className="text-gray-300 text-sm mr-2 hidden sm:inline">
                Hi, {user.firstName || user.userName}!
              </span>
            )}
            <button
              type="button"
              className="bg-gray-800 inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white transition-colors"
              aria-controls="mobile-menu"
              aria-expanded={isMobileMenuOpen}
              onClick={toggleMobileMenu}
            >
              <span className="sr-only">Open main menu</span>
              <div className="w-6 h-6 flex items-center justify-center">
                <div className="relative w-5 h-5">
                  <span
                    className={`absolute left-0 h-0.5 w-5 bg-current transform transition-all duration-300 ${
                      isMobileMenuOpen ? "rotate-45 top-2" : "top-1"
                    }`}
                  />
                  <span
                    className={`absolute left-0 h-0.5 w-5 bg-current transform transition-all duration-300 top-2 ${
                      isMobileMenuOpen ? "opacity-0" : "opacity-100"
                    }`}
                  />
                  <span
                    className={`absolute left-0 h-0.5 w-5 bg-current transform transition-all duration-300 ${
                      isMobileMenuOpen ? "-rotate-45 top-2" : "top-3"
                    }`}
                  />
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden transition-all duration-300 ease-in-out relative z-50
          ${
            isMobileMenuOpen
              ? "max-h-96 opacity-100" // Increased max-h
              : "max-h-0 opacity-0 overflow-hidden"
          }`}
        id="mobile-menu"
      >
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-gray-800">
          {baseNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`block px-3 py-2 rounded-md text-base font-medium transition-all duration-200
                ${
                  location.pathname === item.path
                    ? "bg-gray-900 text-white"
                    : "text-gray-300 hover:bg-gray-700 hover:text-white"
                }`}
              onClick={() => handleNavClick(item.path)}
            >
              {item.label}
            </Link>
          ))}
          {/* Auth Links - Mobile */}
          {!isLoading &&
            authNavItems.map((item) =>
              item.path ? (
                <Link
                  key={item.label}
                  to={item.path}
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:bg-gray-700 hover:text-white"
                  onClick={() => handleNavClick(item.path)}
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.label}
                  onClick={item.action}
                  className="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:bg-gray-700 hover:text-white"
                >
                  {item.label}
                </button>
              )
            )}
        </div>
      </div>

      {/* Mobile menu backdrop */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40 md:hidden"
          onClick={closeMobileMenu}
        />
      )}
    </nav>
  );
};

export default Navbar;
